import { VersionPill } from '@/components/admin/versioned/version-pill';
import { referentTitle } from '@/lib/versioned-labels';
import type { UsageLine } from '@matter_extractor/shared';
import { VERSION_TAGS } from '@matter_extractor/shared';

/**
 * « Utilisé dans » - the DETAIL the card leaves out (refonte modulaire §4, §6).
 *
 * The card says a count; this panel names every referent and the tags it cites
 * the entity under, which is what an operator needs before moving a pointer or
 * retiring anything. Both come from the same read: « utilisé dans » is
 * computed at read time by walking the references, and there is no usage table
 * to maintain (§4).
 *
 * Every row carries its tags, and the panel's own sentence carries the one
 * thing the rows cannot say: a session already played keeps its freeze. That is
 * the property the whole versioning pattern exists for, and this is the screen
 * where somebody is about to doubt it.
 *
 * ## An empty list renders NOTHING - the panel disappears
 *
 * « Un usage nul ne s'affiche pas » (§4): no protocol cites anything until the
 * protocol lot lands (§19), so a panel that said « nulle part encore » would
 * be a box every editor of every library carries, saying nothing, for weeks.
 * The guard is here rather than at the mount site because this is the only
 * file that can promise the panel never renders empty, whichever consumer of
 * the layer mounts it.
 *
 * The three confirmation boxes keep their own « utilisé nulle part encore »
 * (§4): they answer « qui jouera cette version », and an empty list is an
 * answer there rather than a count.
 */

export interface UsagePanelProps {
  /** The `data-testid` prefix of the object: « critere » (`VersionedWording.entity`). */
  entity: string;
  usage: readonly UsageLine[];
}

export function UsagePanel({ entity, usage }: UsagePanelProps) {
  if (usage.length === 0) return null;
  return (
    <section
      data-testid={`${entity}-utilise-dans`}
      className="space-y-3 rounded-md border-2 border-gray-200 bg-white p-5"
    >
      <div className="space-y-0.5">
        <h2 className="font-semibold text-bleu-nuit text-lg leading-7">Utilisé dans</h2>
        <p className="text-gray-600 text-xs leading-4">
          Seules les versions pointées par latest prod ou latest dev, ou marquées « en prod »,
          comptent. Une session déjà jouée garde son gel.
        </p>
      </div>

      {usage.map((referent) => (
        <div key={`${referent.kind}-${referent.id}`} className="space-y-1">
          <p className="break-words text-gray-800 text-sm">
            {referentTitle(referent)}
            {referent.via === null ? null : (
              <span className="text-gray-600"> par {referent.via}</span>
            )}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {/* Walked in the catalogue's order and not in the order the
                server sent, so two rows saying the same thing say it the
                same way. */}
            {VERSION_TAGS.filter((tag) => referent.tags.includes(tag)).map((tag) => (
              <VersionPill key={tag} entity={entity} tag={tag} />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
